// i18n engine — i18next configured for the platform's two-namespace model.
//
//   ns "legacy" → English string IS the key. Resolves the 8,749 existing
//                 `t("ar","en")` call sites for non-primary languages. ar/en are
//                 fast-pathed in the React shim and never hit i18next.
//   ns "app"    → proper dotted keys with CLDR plural support (`key_one`,
//                 `key_other`, …). All NEW code and any file a refactor touches
//                 should use these via `tk` / `tn`.
//
// Per-locale resource bundles are lazy-loaded from ./locales/<code>.ts and
// registered with i18next on demand, so only the active locale's chunk is
// fetched. en + ar (fallback + default) are bundled eagerly because they are
// always needed.

import i18next from "i18next";

import { DEFAULT_LANGUAGE, FALLBACK_LANGUAGE, isEnabledLanguage } from "./languages";

// Eagerly-bundled primary locales (fallback + default are always required).
import ar from "./locales/ar";
import en from "./locales/en";

export type LocaleBundle = {
  legacy?: Record<string, string>;
  app?: Record<string, unknown>;
};

// Lazy importers for every non-eager locale. Vite code-splits each into its own
// content-hashed chunk; the bundle is only fetched when the locale is selected.
const LAZY_LOCALES: Record<string, () => Promise<{ default: LocaleBundle }>> = {
  ur: () => import("./locales/ur"),
  fa: () => import("./locales/fa"),
  hi: () => import("./locales/hi"),
  zh: () => import("./locales/zh"),
  "zh-TW": () => import("./locales/zh-TW"),
  ja: () => import("./locales/ja"),
  ko: () => import("./locales/ko"),
  ms: () => import("./locales/ms"),
  id: () => import("./locales/id"),
  ta: () => import("./locales/ta"),
  fr: () => import("./locales/fr"),
  de: () => import("./locales/de"),
  es: () => import("./locales/es"),
  pt: () => import("./locales/pt"),
  ru: () => import("./locales/ru"),
  tr: () => import("./locales/tr"),
  th: () => import("./locales/th"),
  vi: () => import("./locales/vi"),
};

const loadedLocales = new Set<string>(["ar", "en"]);

let initialized = false;

export function initI18n(initialLanguage: string): Promise<unknown> {
  if (initialized) return Promise.resolve();
  initialized = true;
  return i18next.init({
    lng: initialLanguage,
    fallbackLng: FALLBACK_LANGUAGE,
    defaultNS: "app",
    ns: ["app", "legacy"],
    // Synchronous init (i18next v26 `initAsync: false`): resources below are
    // registered before init() returns, so the very first React render already
    // resolves strings — no null gate, no blank tree.
    initAsync: false,
    resources: {
      en: { app: en.app ?? {}, legacy: en.legacy ?? {} },
      ar: { app: ar.app ?? {}, legacy: ar.legacy ?? {} },
    },
    interpolation: { escapeValue: false }, // React already escapes.
    returnNull: false,
  });
}

// Eager, synchronous init at module load — the engine is ready before any
// component renders, so the provider never has to init during its render body.
// The initial language is irrelevant to resolution (every resolver passes `lng`
// explicitly); en/ar bundles are registered here, lazy locales load on demand.
initI18n(DEFAULT_LANGUAGE);

/** Ensure a locale's bundle is registered with i18next before switching to it. */
export async function ensureLocaleLoaded(code: string): Promise<void> {
  if (loadedLocales.has(code)) return;
  const importer = LAZY_LOCALES[code];
  if (!importer) return; // ar/en (eager) or unknown — nothing to fetch.
  const mod = await importer();
  const bundle = mod.default;
  if (bundle.legacy) i18next.addResourceBundle(code, "legacy", bundle.legacy, true, true);
  if (bundle.app) i18next.addResourceBundle(code, "app", bundle.app, true, true);
  loadedLocales.add(code);
}

/** Switch i18next to a (already-loaded) language. */
export async function changeI18nLanguage(code: string): Promise<void> {
  await ensureLocaleLoaded(code);
  await i18next.changeLanguage(code);
}

// ---------------------------------------------------------------------------
// Resolution helpers — consumed by LanguageContext, not called directly in
// components (components use `useLanguage()`).
// ---------------------------------------------------------------------------

// All resolvers pass `lng` explicitly to i18next so resolution depends on the
// caller's language, never on the singleton's global current language (which can
// drift across React remounts or between tests). `lng` defaults to undefined,
// in which case i18next falls back to its current language.

/**
 * Legacy bilingual resolver. ar/en are returned inline with zero lookup; any
 * other active language resolves the English string through the `legacy`
 * namespace, falling back to the English source when a key is missing.
 */
export function resolveLegacy(language: string, ar_: string, en_: string): string {
  if (language === "ar") return ar_;
  if (language === "en") return en_;
  return i18next.t(en_, { ns: "legacy", lng: language, defaultValue: en_ }) as string;
}

/** Keyed lookup in the `app` namespace (no pluralization). */
export function resolveKey(key: string, vars?: Record<string, unknown>, lng?: string): string {
  // Spread caller vars FIRST so the engine's control options (ns, lng) always
  // win — an interpolation var named `lng`/`ns` must not hijack resolution.
  // `lng` is set unconditionally (i18next reads `lng: undefined` as "use the
  // active language"), so a `vars.lng` can't survive even when no lng is passed.
  return i18next.t(key, { ...vars, ns: "app", lng }) as string;
}

/** Plural-aware keyed lookup — picks `key_one`/`key_other`/… per CLDR rules. */
export function resolvePlural(
  key: string,
  count: number,
  vars?: Record<string, unknown>,
  lng?: string,
): string {
  // vars first (see resolveKey) so count/ns/lng can't be clobbered by a caller var.
  return i18next.t(key, { ...vars, ns: "app", count, lng }) as string;
}

export { isEnabledLanguage, DEFAULT_LANGUAGE };
export { i18next };
